"use client";

import { Report, ReportStatus, ReportUpdate } from "@/types";
import { ReportStatusBadge } from "./ReportStatusBadge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  formatDateTime,
  formatReportCategory,
  getEntityTypeLabel,
} from "@/lib/utils";
import { useState } from "react";
import { ReportsAPI } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { CheckCircle, XCircle } from "lucide-react";

interface ReportDetailViewProps {
  report: Report;
  onReportUpdated: () => void;
}

export function ReportDetailView({
  report,
  onReportUpdated,
}: ReportDetailViewProps) {
  const [adminNote, setAdminNote] = useState(report.admin_note || "");
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<"resolve" | "reject">("resolve");

  const handleStatusUpdate = async (status: ReportStatus) => {
    setIsUpdating(true);
    try {
      await ReportsAPI.update(report.id, {
        status,
        admin_note: adminNote,
      });
      toast.success("Report status updated successfully");
      onReportUpdated();
    } catch (error) {
      console.error("Error updating report:", error);
      toast.error("Failed to update report status");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleResolve = async () => {
    setIsUpdating(true);
    try {
      await ReportsAPI.resolve(report.id, adminNote);
      toast.success("Report resolved successfully");
      onReportUpdated();
    } catch (error) {
      console.error("Error resolving report:", error);
      toast.error("Failed to resolve report");
    } finally {
      setIsUpdating(false);
      setIsDialogOpen(false);
    }
  };

  const handleReject = async () => {
    setIsUpdating(true);
    try {
      await ReportsAPI.reject(report.id, adminNote);
      toast.success("Report rejected successfully");
      onReportUpdated();
    } catch (error) {
      console.error("Error rejecting report:", error);
      toast.error("Failed to reject report");
    } finally {
      setIsUpdating(false);
      setIsDialogOpen(false);
    }
  };

  const handleAction = () => {
    if (actionType === "resolve") {
      handleResolve();
    } else {
      handleReject();
    }
  };

  const openResolveDialog = () => {
    setActionType("resolve");
    setIsDialogOpen(true);
  };

  const openRejectDialog = () => {
    setActionType("reject");
    setIsDialogOpen(true);
  };

  const isFinalState = ["resolved", "rejected", "invalid"].includes(
    report.status
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="text-xl font-semibold">Report #{report.id}</h2>
        <ReportStatusBadge status={report.status} />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Report Details</CardTitle>
            <CardDescription>Information about this report</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Entity Type
              </p>
              <p>{getEntityTypeLabel(report.entity_type)}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Entity ID
              </p>
              <p>{report.entity_id}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Category
              </p>
              <p>{formatReportCategory(report.category)}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Reported By
              </p>
              <p>
                User #{report.reported_by}
                {report.is_anonymous && " (Anonymously)"}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Submitted
              </p>
              <p>{formatDateTime(report.created_date)}</p>
            </div>
            {report.severity_level !== undefined && (
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Severity Level
                </p>
                <p>{report.severity_level}/5</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Report Content</CardTitle>
            <CardDescription>Details provided by the reporter</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {report.description ? (
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Description
                </p>
                <p className="whitespace-pre-wrap rounded-md bg-muted p-3">
                  {report.description}
                </p>
              </div>
            ) : (
              <p className="text-muted-foreground">No description provided</p>
            )}

            {report.evidence_urls && report.evidence_urls.length > 0 && (
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Evidence
                </p>
                <ul className="list-inside list-disc space-y-1">
                  {report.evidence_urls.map((url, index) => (
                    <li key={index}>
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        Evidence #{index + 1}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Admin Actions</CardTitle>
          <CardDescription>Review and respond to this report</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <p className="mb-2 text-sm font-medium">Admin Notes</p>
              <Textarea
                placeholder="Add your notes here..."
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                disabled={isFinalState}
                className="min-h-24"
              />
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex justify-between">
          {!isFinalState ? (
            <>
              <Button
                variant="outline"
                onClick={() => handleStatusUpdate("processing")}
                disabled={isUpdating}
              >
                Mark as Processing
              </Button>
              <div className="space-x-2">
                <Button
                  variant="destructive"
                  onClick={() => handleStatusUpdate("rejected")}
                  disabled={isUpdating}
                >
                  <XCircle className="mr-1 h-4 w-4" />
                  Reject
                </Button>
                <Button
                  onClick={() => handleStatusUpdate("resolved")}
                  disabled={isUpdating}
                >
                  <CheckCircle className="mr-1 h-4 w-4" />
                  Resolve
                </Button>
              </div>
            </>
          ) : (
            <p className="italic text-muted-foreground">
              This report has been {report.status}.
              {report.resolved_by &&
                ` Action taken by admin #${report.resolved_by}.`}
              {report.resolved_date &&
                ` Finalized on ${formatDateTime(report.resolved_date)}.`}
            </p>
          )}
        </CardFooter>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === "resolve" ? "Resolve Report" : "Reject Report"}
            </DialogTitle>
            <DialogDescription>
              {actionType === "resolve"
                ? "Are you sure you want to resolve this report? This action cannot be undone."
                : "Are you sure you want to reject this report? This action cannot be undone."}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="mb-2 text-sm">Admin Notes:</p>
            <p className="rounded-md bg-muted p-3">
              {adminNote || "(No notes added)"}
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDialogOpen(false)}
              disabled={isUpdating}
            >
              Cancel
            </Button>
            <Button
              variant={actionType === "resolve" ? "default" : "destructive"}
              onClick={handleAction}
              disabled={isUpdating}
            >
              {isUpdating
                ? "Processing..."
                : actionType === "resolve"
                ? "Resolve Report"
                : "Reject Report"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
